import { useCallback, useRef } from 'react';

import { PROMPT_INSTRUCTIONS_ACTION } from 'src/components/PromptInstructions';
import { PROMPT_MENU_TYPE } from 'src/components/RichSlate';
import { MAX_RESOURCE_PER_TYPE } from 'src/data/common';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { RESOURCE_CONTENT_TYPE, RESOURCE_STATUS } from 'src/data/performance';
import * as events from 'src/lib/events';
import {
  useBrandVoicesActions,
  useBrandVoicesAssets,
  useCustomerActions,
  useFreestyleToolsActions,
  useNotificationActions,
} from 'src/store';
import { useBrandBriefPopup } from 'src/views/BrandBriefPopup';
import { useUpgradePopup } from 'src/views/UpgradePopup';

import { useCustomerDocumentLabelMp4Enabled } from '../customer';
import { useIsApplicationModeInfinity } from '../useIsApplicationModeInfinity';
import { useDocumentResources, useImageResources, useUrlResources } from '../useResources';
import { useTrackingEvent } from '../useTrackingEvent';
import { useUploadResourceFile } from '../useUploadResourceFile';

import { useExternalChannelMenuProps } from './useExternalChannelMenuProps';

export const usePreparePromptInstructionsProps = ({
  eventPage,
  generationTool,
  uploadingImages = [],
  uploadingDocuments = [],
  currentUrls = [],
  provideUrlResources = false,
  provideDocumentResources = false,
  provideImageResources = false,
  setIsUploadingDocument,
  setIsUploadingImage,
  setDocumentError,
  setImageError,
  clearDocumentError,
  clearImageError,
  setDocumentUploadSuccess,
  setImageUploadSuccess,
  onAction,
}) => {
  const isApplicationModeInfinity = useIsApplicationModeInfinity();
  const externalChannelMenuProps = useExternalChannelMenuProps();
  const customerDocumentLabelMp4Enabled = useCustomerDocumentLabelMp4Enabled();

  const { brandBriefs } = useBrandVoicesAssets();
  const urlResources = useUrlResources();
  const documentResources = useDocumentResources();
  const imageResources = useImageResources();

  const notifications = useNotificationActions();
  const customerActions = useCustomerActions();
  const brandVoicesActions = useBrandVoicesActions();
  const freestyleToolsActions = useFreestyleToolsActions();
  const openUpgradePopup = useUpgradePopup();
  const openBrandBriefPopup = useBrandBriefPopup();
  const trackEvent = useTrackingEvent();
  const uploadResourceFile = useUploadResourceFile();

  const shouldOpenConfirmPopup = useCallback(
    (resourceTypes, id) => {
      // go over types and check if a file exists for one of the types
      for (const type of resourceTypes) {
        if (
          type === RESOURCE_CONTENT_TYPE.image &&
          uploadingImages.length === MAX_RESOURCE_PER_TYPE
        ) {
          return !id || !uploadingImages.some((image) => image.id === id);
        }
        if (
          type === RESOURCE_CONTENT_TYPE.document &&
          uploadingDocuments.length === MAX_RESOURCE_PER_TYPE
        ) {
          return !id || !uploadingDocuments.some((doc) => doc.id === id);
        }
        if (type === RESOURCE_CONTENT_TYPE.url && currentUrls.length === MAX_RESOURCE_PER_TYPE) {
          return true;
        }
      }
      return false;
    },
    [currentUrls.length, uploadingDocuments, uploadingImages]
  );

  /**
    "uploadResourceFile" action handler
   */
  const handleUploadResourceFileActionRef = useRef();
  handleUploadResourceFileActionRef.current = async ({ resourceType, file, randomId, index }) => {
    // Reflect the "start" of the upload process
    switch (resourceType) {
      case RESOURCE_CONTENT_TYPE.image:
        setIsUploadingImage(true, randomId, index, file.name);
        break;
      case RESOURCE_CONTENT_TYPE.document:
        setIsUploadingDocument(true, file.name, randomId, index);
        break;
      default:
        break;
    }

    try {
      const resource = await uploadResourceFile({ resourceType, file });

      if (resource.status === RESOURCE_STATUS.active) {
        switch (resourceType) {
          case RESOURCE_CONTENT_TYPE.image:
            clearImageError();
            setImageUploadSuccess(resource);
            break;
          case RESOURCE_CONTENT_TYPE.document:
            clearDocumentError();
            setDocumentUploadSuccess(resource);
            break;
          default:
            break;
        }
      }

      return resource;
    } catch (error) {
      switch (resourceType) {
        case RESOURCE_CONTENT_TYPE.image:
          onAction(PROMPT_INSTRUCTIONS_ACTION.removeUploadingImage, { itemId: randomId });
          setImageError(null, error);
          break;
        case RESOURCE_CONTENT_TYPE.document:
          onAction(PROMPT_INSTRUCTIONS_ACTION.removeUploadingDocument, { itemId: randomId });
          setDocumentError(null, error);
          break;
        default:
          break;
      }
    } finally {
      // Reflect the "finish" of the upload process
      switch (resourceType) {
        case RESOURCE_CONTENT_TYPE.image:
          setIsUploadingImage(false);
          break;
        case RESOURCE_CONTENT_TYPE.document:
          setIsUploadingDocument(false);
          break;
        default:
          break;
      }
    }
  };

  const handleAction = useCallback(
    (action, data = null) => {
      switch (action) {
        case PROMPT_INSTRUCTIONS_ACTION.addBrandBrief: {
          if (customerActions.featureLimitExceeded(FEATURE_LIMITS.brandBriefPerCustomer)) {
            openUpgradePopup(FEATURE_LIMITS.brandBriefPerCustomer);
            return;
          }

          const brandVoice = data?.brandVoice || brandVoicesActions.getDefaultBrandVoice();
          return openBrandBriefPopup({ brandVoice });
        }

        case PROMPT_INSTRUCTIONS_ACTION.insertBrandBrief: {
          trackEvent(events.PROMPTS.promptMessageBankInserted, {
            page: eventPage,
            generationTool,
            widget: data?.widget,
          });

          if (data?.menuType === PROMPT_MENU_TYPE.product) {
            freestyleToolsActions.setLastPromptAsset(data.item);
          }
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.insertUrl: {
          trackEvent(events.PROMPTS.promptResourceInserted, {
            page: eventPage,
            generationTool,
            type: RESOURCE_CONTENT_TYPE.url,
            widget: data?.widget,
          });
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.insertDocument: {
          trackEvent(events.PROMPTS.promptResourceInserted, {
            page: eventPage,
            generationTool,
            type: RESOURCE_CONTENT_TYPE.document,
            widget: data?.widget,
          });
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.insertImage: {
          trackEvent(events.PROMPTS.promptResourceInserted, {
            page: eventPage,
            generationTool,
            type: RESOURCE_CONTENT_TYPE.image,
            widget: data?.widget,
          });
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.displayMessage: {
          notifications.displayMessage(data.message);
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.displayError: {
          notifications.displayError(data);
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.trackEvent: {
          const { event, ...attributes } = data;
          trackEvent(event, {
            page: eventPage,
            generationTool,
            ...attributes,
          });
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.uploadResourceFile: {
          return handleUploadResourceFileActionRef.current(data);
        }

        default:
          break;
      }

      // Next, pass action to parent component
      return onAction?.(action, data);
    },
    [
      onAction,
      customerActions,
      brandVoicesActions,
      openBrandBriefPopup,
      openUpgradePopup,
      trackEvent,
      eventPage,
      generationTool,
      freestyleToolsActions,
      notifications,
    ]
  );

  return {
    isApplicationModeInfinity,
    externalChannelMenuProps,
    brandBriefs,
    urlResources: provideUrlResources ? urlResources : [],
    documentResources: provideDocumentResources ? documentResources : [],
    imageResources: provideImageResources ? imageResources : [],
    shouldOpenConfirmPopup,
    onAction: handleAction,
    shouldIncludeMp4: customerDocumentLabelMp4Enabled,
  };
};
